import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { GameMessageEvent, GameState } from "../../../core/protocol/types";
import OtherPlayersView from "./OtherPlayersView";
import ScoreboardOverlay from "./ScoreboardOverlay";
import GameMessage from "./GameMessage";
import Settings from "../Settings";
import TouchControls from "../TouchControls";
import { clearGameConnection } from "../../utils/playerIdentity";
import { useMessageQueue } from "./hooks/useMessageQueue";
import { initializeAnnouncerAudio } from "./messageAnimations";
import { GameRuntimeCallbacks } from "../../runtime/controller/GameRuntimeController";
import KeyboardInputManager, {
  loadConfig,
} from "../../managers/keyboard-input-manager";
import TouchInputManager from "../../managers/touch-input-manager";
import { MultiplayerRuntimeHandle } from "../../runtime/handle/MultiplayerRuntimeHandle";
import { GameController } from "../../controller/GameController";
import "./Game.css";

interface GameStats {
  score: number;
  level: number;
  lines: number;
}

interface GameProps {
  runtimeHandle: MultiplayerRuntimeHandle;
  gameController: GameController;
  gameState: GameState | null;
}

const Game: React.FC<GameProps> = ({
  runtimeHandle,
  gameController,
  gameState,
}) => {
  const navigate = useNavigate();

  const [localStats, setLocalStats] = useState<GameStats>({
    score: 0,
    level: 1,
    lines: 0,
  });
  const [showMenu, setShowMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [viewReady, setViewReady] = useState(false);
  const otherPlayersSectionRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [otherPlayersDimensions, setOtherPlayersDimensions] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const updateDimensions = () => {
      if (otherPlayersSectionRef.current) {
        const rect = otherPlayersSectionRef.current.getBoundingClientRect();
        setOtherPlayersDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateDimensions);
      });
    }, 100);

    let resizeObserver: ResizeObserver | null = null;
    if (otherPlayersSectionRef.current) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateDimensions);
      });
      resizeObserver.observe(otherPlayersSectionRef.current);
    }

    window.addEventListener("resize", updateDimensions);

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const calculateCanvasSize = useCallback(() => {
    if (!canvasContainerRef.current) return 800;
    const container = canvasContainerRef.current;
    const containerHeight = container.clientHeight;
    const containerWidth = container.clientWidth;

    // Calculate max width based on height
    // GameView aspect ratio is approximately Height = 1.3 * Width
    // So Width = Height / 1.3
    const maxWidthFromHeight = containerHeight / 1.3;
    
    // Fit within container width and height-derived width
    const canvasWidth = Math.min(containerWidth, maxWidthFromHeight);

    // Enforce min render width of 800 for visual quality (CSS will scale it down)
    return Math.max(800, canvasWidth);
  }, []);

  const {
    activeMessage,
    addMessages: addToMessageQueue,
    removeMessage: removeFromMessageQueue,
  } = useMessageQueue<GameMessageEvent>({
    maxQueueSize: 2,
  });

  const gameControllerRef = React.useRef(gameController);
  const showMenuRef = React.useRef(false);
  const showSettingsRef = React.useRef(false);
  const tabKeyPressedRef = useRef(false);
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const lastProcessedMessageCountRef = useRef<number>(0);
  const touchInputRef = useRef<any>(null);

  const remoteGameManager = runtimeHandle.managers.remote;
  const localGameManager = runtimeHandle.managers.local;
  useEffect(() => {
    const localGame = localGameManager?.getLocalGame();
    if (localGame?.audioManager) {
      initializeAnnouncerAudio(localGame.audioManager);
    }
  }, [localGameManager, gameState]);

  const getRemoteGame = useCallback(
    (numPlayer: number) => {
      return remoteGameManager
        ? remoteGameManager.getRemoteGameByNumPlayer(numPlayer)
        : undefined;
    },
    [remoteGameManager]
  );


  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) ||
      window.matchMedia("(pointer: coarse)").matches
    );
  };

  useEffect(() => {
    gameControllerRef.current = gameController;

    if (gameController) {
      gameController.setOnJoinRejected((reason: string) => {
        console.error("Join rejected:", reason);

        clearGameConnection();

        navigate("/multi-player", {
          state: {
            error: reason || "Failed to join game. Server may be full.",
          },
        });
      });
    }
  }, [gameController, navigate]);

  useEffect(() => {
    if (gameState?.status === "finished") {
      clearGameConnection();
    }

    return () => {
      clearGameConnection();
    };
  }, [gameState?.status]);

  // Game loop is now handled by MultiplayerGameLoop class

  // Create input sources (keyboard + touch)
  const inputSources = useMemo(() => {
    const keyboardInput = new KeyboardInputManager(document, loadConfig(), true);
    const touchInput = new TouchInputManager();
    touchInputRef.current = touchInput; // Store for TouchControls
    return [keyboardInput, touchInput];
  }, []);

  useEffect(() => {
    runtimeHandle.input.setSources(inputSources);
    return () => {
      runtimeHandle.input.clearSources();
    };
  }, [runtimeHandle, inputSources]);

  // Setup keyboard event listener
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (showMenuRef.current || showSettingsRef.current) {
        return;
      }

      // Don't process TAB key here - it's handled by handleTabKeyDown
      if (event.key === "Tab" || event.keyCode === 9) {
        return;
      }

      if (event.key === "Escape") {
        return;
      }

      if (gameControllerRef.current) {
        gameControllerRef.current.handlePlayerInput(event.keyCode);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Setup resize observer (after view is initialized)
  useEffect(() => {
    if (!viewReady || !canvasContainerRef.current) {
      return;
    }

    let resizeRAF: number;

    const handleResize = () => {
      // Debounce resize to avoid ResizeObserver loop errors
      cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(() => {
        runtimeHandle.resizeGameView();
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    
    resizeObserver.observe(canvasContainerRef.current);
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(resizeRAF);
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [viewReady, runtimeHandle]);

  const getCanvasElement = useCallback(
    () => document.getElementById("canvas") as HTMLCanvasElement | null,
    []
  );

  const hasRuntimePrereqs =
    !!gameState?.localConnectionId &&
    !!gameState?.gameMode &&
    gameState?.gameSeed !== undefined;

  // Initialize game loop (only when canvas is mounted and data ready)
  useEffect(() => {
    if (!gameController || !hasRuntimePrereqs) {
      return;
    }

    let disposed = false;
    let started = false;

    const callbacks: GameRuntimeCallbacks = {
      onStatsUpdate: (stats) => {
        setLocalStats(stats);
      },
      onGameInitialized: () => {
        setViewReady(true);
      },
      onViewReady: () => setViewReady(true),
      onViewDisposed: () => setViewReady(false),
    };

    const ensureCanvasAndStart = () => {
      if (disposed || started) {
        return;
      }
      const canvas = getCanvasElement();
      if (!canvas) {
        requestAnimationFrame(ensureCanvasAndStart);
        return;
      }

      runtimeHandle.startGameLoop({
        getCanvasElement,
        getCanvasSize: calculateCanvasSize,
        callbacks,
      });
      started = true;
    };

    ensureCanvasAndStart();

    return () => {
      disposed = true;
      if (started) {
        runtimeHandle.stopGameLoop();
        setViewReady(false);
      }
    };
  }, [
    runtimeHandle,
    gameController,
    calculateCanvasSize,
    getCanvasElement,
    hasRuntimePrereqs,
  ]);

  useEffect(() => {
    const handleTabKeyDown = (event: KeyboardEvent) => {
      if (showMenuRef.current || showSettingsRef.current) {
        return;
      }

      if (event.key === "Tab" || event.keyCode === 9) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        // Always set showScoreboard to true when TAB is detected (even on repeated keydown events)
        // This ensures the scoreboard stays visible while TAB is held
        if (!tabKeyPressedRef.current) {
          tabKeyPressedRef.current = true;
        }
        setShowScoreboard(true);
        return false;
      }
    };

    const handleTabKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Tab" || event.keyCode === 9) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        tabKeyPressedRef.current = false;
        setShowScoreboard(false);
        return false;
      }
    };

    document.addEventListener("keydown", handleTabKeyDown, true);
    document.addEventListener("keyup", handleTabKeyUp, true);
    window.addEventListener("keydown", handleTabKeyDown, true);
    window.addEventListener("keyup", handleTabKeyUp, true);

    return () => {
      document.removeEventListener("keydown", handleTabKeyDown, true);
      document.removeEventListener("keyup", handleTabKeyUp, true);
      window.removeEventListener("keydown", handleTabKeyDown, true);
      window.removeEventListener("keyup", handleTabKeyUp, true);
    };
  }, []);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (showSettings) {
          setShowSettings(false);
        } else {
          setShowMenu((prev) => {
            showMenuRef.current = !prev;
            return !prev;
          });
        }
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [showSettings]);

  useEffect(() => {
    showMenuRef.current = showMenu;
  }, [showMenu]);

  useEffect(() => {
    showSettingsRef.current = showSettings;
  }, [showSettings]);

  // Countdown audio is now handled by MultiplayerGameLoop

  useEffect(() => {
    if (!gameState?.gameMessages || gameState.gameMessages.length === 0) {
      lastProcessedMessageCountRef.current = 0;
      return;
    }

    // Process new messages silently

    const newMessages: GameMessageEvent[] = [];
    const localConnectionId = gameState.localConnectionId;

    gameState.gameMessages.forEach((message) => {
      if (!processedMessageIdsRef.current.has(message.id)) {
        if (message.type === "frag" || message.type === "firstBlood" || message.type === "multiKill" || message.type === "killStreak") {
          processedMessageIdsRef.current.add(message.id);
          newMessages.push(message);
        } else if (
          message.type === "roundWin" ||
          message.type === "roundLose"
        ) {
          if (localConnectionId && message.id.includes(localConnectionId)) {
            processedMessageIdsRef.current.add(message.id);
            newMessages.push(message);
          }
        }
      }
    });

    if (newMessages.length > 0) {
      addToMessageQueue(newMessages);
    }

    lastProcessedMessageCountRef.current = gameState.gameMessages.length;
  }, [gameState?.gameMessages, gameState?.localConnectionId, addToMessageQueue]);


  const handleGameMessageComplete = useCallback(
    (id: string) => {
      removeFromMessageQueue(id);
    },
    [removeFromMessageQueue]
  );

  // Memoize the onComplete handler per message ID to prevent re-renders
  const getOnCompleteHandler = useCallback((messageId: string) => {
    return () => handleGameMessageComplete(messageId);
  }, [handleGameMessageComplete]);

  if (!hasRuntimePrereqs) {
    return (
      <div className="multiplayer-game">
        <div className="game-menu-overlay">
          <div className="game-menu">
            <h2>Waiting for game data…</h2>
            <p>Waiting for host to provide game mode and seed.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="multiplayer-game">
      {showSettings && (
        <div className="game-menu-overlay">
          <Settings
            onClose={() => setShowSettings(false)}
            showCloseButton={true}
          />
        </div>
      )}
      {showMenu && !showSettings && (
        <div className="game-menu-overlay" onClick={() => setShowMenu(false)}>
          <div className="game-menu" onClick={(e) => e.stopPropagation()}>
            <h2>Game Menu</h2>
            <button className="menu-button" onClick={() => navigate("/")}>
              Exit to Main Menu
            </button>
            <button
              className="menu-button"
              onClick={() => {
                setShowMenu(false);
                setShowSettings(true);
              }}
            >
              Options
            </button>
            <button className="menu-button" onClick={() => setShowMenu(false)}>
              Resume Game
            </button>
          </div>
        </div>
      )}
      {showScoreboard && (
        <ScoreboardOverlay gameState={gameState} localStats={localStats} />
      )}
      {/* Game Messages Overlay */}
      <div className="game-messages-container">
        {/* View layer handles rendering: only show the active message */}
        {activeMessage &&
          (() => {
            const message = activeMessage;
            const localPlayer = gameState?.connections.get(gameState.localConnectionId);
            const localTeam = localPlayer?.team ?? 0;

            return (
              <GameMessage
                key={message.id}
                message={message}
                localConnectionId={gameState?.localConnectionId || ""}
                localTeam={localTeam}
                gameState={gameState!}
                onComplete={getOnCompleteHandler(message.id)}
              />
            );
          })()}
      </div>
      {/* Other Players - At the top */}
      {gameState && gameState.players && (
        <div className="other-players-section" ref={otherPlayersSectionRef}>
          <OtherPlayersView
            players={gameState.players}
            localConnectionId={gameState.localConnectionId}
            palette={localGameManager?.getLocalGame()?.palette}
            containerWidth={otherPlayersDimensions.width}
            containerHeight={otherPlayersDimensions.height}
            getRemoteGame={getRemoteGame}
          />
        </div>
      )}

      <div className="game-container">
        <div className="local-game" ref={canvasContainerRef}>
          {(() => {
            return (
              <canvas
                id="canvas"
                style={{}}
              />
            );
          })()}
        </div>
      </div>

      {/* Touch Controls for Mobile */}
      {isMobileDevice() &&
        touchInputRef.current &&
        gameState?.status === "active" &&
        !showMenu &&
        !showSettings && (
          <TouchControls touchInput={touchInputRef.current} />
        )}
    </div>
  );
};

export default Game;
